import React, { useMemo, useState, useEffect } from "react";
import Modal from "../ui/Modal";

const input = { padding:"10px 12px", borderRadius:8, border:"1px solid #ddd", background:"#fff", fontSize:14 };
const area  = { ...input, resize:"vertical" };
const row   = { display:"grid", gridTemplateColumns:"160px 1fr", alignItems:"center", gap:12 };

// spaces->underscore, lowercase, strip non [a-z0-9_], collapse & trim underscores
const sanitizeCode = (v) =>
  (v || "")
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

export default function AddSubcategoryModal({ open, onClose, onCreate, parentCategory }) {
  const [form, setForm] = useState({
    main: parentCategory?.name || "",
    code: parentCategory?.code || "",
    category: "",   // subcategory display name (required)
    subcode: "",    // subcategory code (required, sanitized)
    description: "",
    visibility: 1,
  });

  useEffect(() => {
    setForm((p)=>({
      ...p,
      main: parentCategory?.name || "",
      code: parentCategory?.code || "",
    }));
  }, [parentCategory]);

  const [touched, setTouched] = useState({ category:false, subcode:false });
  const [saving, setSaving] = useState(false);

  // setters (sanitize subcode live)
  const set = (k, v) => {
    setForm((p) => {
      if (k === "subcode") v = sanitizeCode(v);
      return { ...p, [k]: v };
    });
  };

  // optional UX: if name filled and subcode empty, prefill subcode once from name
  useEffect(() => {
    if (form.category && !form.subcode) {
      setForm((p) => ({ ...p, subcode: sanitizeCode(form.category) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.category]);

  const errors = {
    category: !form.category?.trim() ? "Subcategory name is required" : "",
    subcode: !form.subcode?.trim() ? "Subcategory code is required" : "",
  };
  const disabled = useMemo(
    () => !!errors.category || !!errors.subcode,
    [errors.category, errors.subcode]
  );

  const submit = async (e) => {
    e?.preventDefault?.();
    setTouched({ category:true, subcode:true });
    if (disabled || saving) return;

    setSaving(true);
    try {
      await onCreate?.(form);
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  const touch = (k) => setTouched((t) => ({ ...t, [k]: true }));

  return (
    <Modal
      open={open}
      onClose={() => !saving && onClose?.()}
      title="Add Subcategory"
      footer={
        <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
          <button disabled={saving} onClick={onClose} style={styles.btn}>Cancel</button>
          <button disabled={saving || disabled} onClick={submit} style={styles.primary}>
            {saving ? "Saving…" : "Add Subcategory"}
          </button>
        </div>
      }
    >
      <form onSubmit={submit} style={{ display:"grid", gap:12 }}>
        <div style={row}>
          <label>Parent (Main)</label>
          <input value={form.main} readOnly style={{ ...input, background:"#f7f7f7" }} />
        </div>

        <div style={row}>
          <label>Parent Code</label>
          <input value={form.code} readOnly style={{ ...input, background:"#f7f7f7" }} />
        </div>

        <div style={row}>
          <label>Subcategory Name<span style={{color:"#b3261e"}}>*</span></label>
          <div>
            <input
              value={form.category}
              onChange={(e)=>set("category", e.target.value)}
              onBlur={()=>touch("category")}
              style={{ ...input, borderColor: touched.category && errors.category ? "#b3261e" : "#ddd" }}
              aria-invalid={!!(touched.category && errors.category)}
              placeholder="e.g., Coffees"
            />
            {touched.category && errors.category && (
              <div style={styles.err}>{errors.category}</div>
            )}
          </div>
        </div>

        <div style={row}>
          <label>Subcategory Code<span style={{color:"#b3261e"}}>*</span></label>
          <div>
            <input
              value={form.subcode}
              onChange={(e)=>set("subcode", e.target.value)}
              onBlur={()=>touch("subcode")}
              style={{ ...input, fontFamily:"monospace", borderColor: touched.subcode && errors.subcode ? "#b3261e" : "#ddd" }}
              aria-invalid={!!(touched.subcode && errors.subcode)}
              placeholder="auto-fills from name, can edit"
            />
            <div style={{ fontSize:12, color:"#666", marginTop:4 }}>
              Stored lowercase; spaces & symbols become <code>_</code>.
            </div>
            {touched.subcode && errors.subcode && (
              <div style={styles.err}>{errors.subcode}</div>
            )}
          </div>
        </div>

        <div style={row}>
          <label>Description</label>
          <textarea
            rows={3}
            value={form.description}
            onChange={(e)=>set("description", e.target.value)}
            style={area}
            placeholder="Optional"
          />
        </div>

        <div style={row}>
          <label>Visible</label>
          <input
            type="checkbox"
            checked={!!form.visibility}
            onChange={(e)=>set("visibility", e.target.checked ? 1 : 0)}
          />
        </div>
      </form>
    </Modal>
  );
}

const styles = {
  btn: { padding:"8px 12px", borderRadius:8, border:"1px solid #ddd", background:"#fff", cursor:"pointer" },
  primary: { padding:"8px 12px", borderRadius:8, border: '1px solid rgba(255, 0, 0, 1)',
  background: 'rgba(255, 0, 0, 1)', color:"#fff", cursor:"pointer" },
  err: { color:"#b3261e", fontSize:12, marginTop:4 }
};
